import axios from 'axios';
import attachAuthToken from '../axiosConfig';
import {
  AprovacaoDetalhe,
  AprovacaoFilaItem,
  AuditoriaCotacoesBusca,
  AuditoriaConvites,
  AuditoriaEscolhasAtuais,
  AuditoriaPedidos,
  AuditoriaProposta,
  AuditoriaTimeline,
  Comparacao,
  CotacaoDetalhe,
  CotacaoItensLote,
  CotacaoListResponse,
  CotacaoAnexo,
  CotacaoPublicaValidacao,
  DecisaoPayload,
  EmailFornecedorEnviado,
  EnvioDeEmail,
  Fornecedor,
  GestorCompras,
  LinkVigente,
  NotificacoesResposta,
  PainelAlertas,
  PedidoGerado,
  Preset,
  PropostaPublicaItem,
  RespostaVerificacaoPublica,
  RespostaConvite,
  RespostaEnvio,
  RespostaEnvioWhatsapp,
  StatusOctadesk,
  SolicitacoesResponse,
  SupervisaoFinalizadasResposta,
  SupervisaoResposta,
} from '../../interfaces/PortalCotacoes';
import { clienteDoLogGeral } from '../logGeral/clienteDoLogGeral';

/**
 * O Portal de Cotações só existe na UI quando a env aponta para o backend dele.
 *
 * É este o mecanismo que mantém o módulo INVISÍVEL EM PRODUÇÃO (decisão do usuário,
 * 11/08/2026): o build de produção injeta apenas `REACT_APP_BACKEND_URL`
 * (`.github/workflows/firebase-hosting-deploy.yml`), então em prod a constante é
 * `false` e menu, hub, rotas internas e rota pública do fornecedor não são
 * registrados. O módulo está barrado para produção pelo ADR 0001 do Portal (a rota
 * de solicitação de compra do Nasajon não existe).
 *
 * Ao liberar o módulo em produção, o passo é declarar a env no workflow de deploy —
 * não há código a "descomentar" (regra de 02/08/2026: nada de tela desativada por
 * bloco comentado).
 *
 * ⚠️ Sem default para `localhost`: um fallback faria o módulo aparecer em produção
 * apontando para a máquina de quem abriu a tela, e o gate viraria decoração.
 */
export const PORTAL_COTACOES_HABILITADO = Boolean(process.env.REACT_APP_PORTAL_COTACOES_URL);

const BASE_URL = process.env.REACT_APP_PORTAL_COTACOES_URL ?? '';

// Instância autenticada (mesmo JWT do SOAC — ADR 0014).
const api = axios.create({ baseURL: BASE_URL });
attachAuthToken(api);

/** Log geral deste serviço (transversal/at-91), lido pela tela Logs do sistema do SOAC. */
export const logGeralDoPortal = clienteDoLogGeral(api);

// Instância SEM token: as rotas do fornecedor não têm JWT — a autorização é o
// token do convite + verificação do CNPJ (ADR 0003 do módulo).
const publicApi = axios.create({ baseURL: BASE_URL });

function extrairMensagem(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    return data?.message || fallback;
  }
  return fallback;
}

/** Erro de API com o `code` do servidor, quando ele manda (at-17: a tela decide pelo código). */
export type ErroDoPortal = Error & { code?: string };

async function req<T>(fn: () => Promise<{ data: T }>, fallback: string): Promise<T> {
  try {
    const { data } = await fn();
    return data;
  } catch (error) {
    const erro: ErroDoPortal = new Error(extrairMensagem(error, fallback));
    if (axios.isAxiosError(error)) {
      const code = (error.response?.data as { code?: unknown } | undefined)?.code;
      if (typeof code === 'string') erro.code = code;
    }
    throw erro;
  }
}

// ── Requisições aprovadas (a entrada, vinda do módulo de Requisições — at-13) ─

export const fetchSolicitacoesAprovadas = (search?: string) =>
  req<SolicitacoesResponse>(
    () => api.get('/solicitacoes/aprovadas', { params: search ? { search } : {} }),
    'Erro ao buscar requisições aprovadas',
  );

// ── Cotações ──────────────────────────────────────────────────────────────────

export const fetchCotacoes = (params: { status?: string; search?: string; page?: number; pageSize?: number }) =>
  req<CotacaoListResponse>(() => api.get('/cotacoes', { params }), 'Erro ao listar cotações');

export const fetchCotacao = (id: string) =>
  req<CotacaoDetalhe>(() => api.get(`/cotacoes/${id}`), 'Erro ao carregar a cotação');

/**
 * Itens de várias cotações numa chamada (13/08/2026 — itens 22 e 20 do backlog).
 *
 * Quem usa: o wizard, para dizer QUAL peça já está cotada e em qual cotação; e a conferência
 * do Orçamento Cilia, para avisar que uma peça removida pelo complemento está numa cotação
 * aberta. A listagem devolve só `totalItens` — daí a rota.
 *
 * Máximo 50 ids (o backend recusa acima disso). Id inexistente é omitido, não é erro.
 */
export const fetchItensEmLote = (ids: string[]) =>
  req<{ data: CotacaoItensLote[] }>(
    () => api.get('/cotacoes/itens-em-lote', { params: { ids: ids.join(',') } }),
    'Erro ao consultar os itens das cotações',
  );

// ── Supervisão do fluxo (item 9 do backlog) ───────────────────────────────────

export const fetchSupervisao = () =>
  req<SupervisaoResposta>(() => api.get('/supervisao'), 'Erro ao carregar a supervisão');

/**
 * Cotações finalizadas (14/08/2026) — a aba "Finalizadas" da Supervisão. Paginada e com
 * busca própria: a lista só cresce, e ninguém rola o histórico inteiro para achar uma.
 */
export const fetchSupervisaoFinalizadas = (busca?: string, page = 1, pageSize = 10) =>
  req<SupervisaoFinalizadasResposta>(
    () => api.get('/supervisao/finalizadas', { params: { busca: busca || undefined, page, pageSize } }),
    'Erro ao carregar as cotações finalizadas',
  );

/**
 * Auditoria de cotações (at-05). Busca própria, e não o `GET /cotacoes`: a tela é aberta
 * ao CHEFE_DE_OFICINA, que não opera cotações — a rota de auditoria devolve só o
 * necessário para achar a cotação e abrir a timeline.
 */
export const fetchAuditoriaCotacoes = (busca?: string, page = 1, limit = 10) =>
  req<AuditoriaCotacoesBusca>(
    () => api.get('/auditoria/cotacoes', { params: { busca: busca || undefined, page, limit } }),
    'Erro ao buscar cotações para auditoria',
  );

export const fetchAuditoriaTimeline = (cotacaoId: string) =>
  req<AuditoriaTimeline>(
    () => api.get(`/auditoria/cotacoes/${cotacaoId}`),
    'Erro ao carregar a auditoria da cotação',
  );

/**
 * Proposta na versão do evento auditado. Rota da auditoria, e não a do detalhe da cotação:
 * aquela é COMPRA/ADMIN/TI e devolve sempre a proposta ATIVA — aqui o CHEFE também alcança,
 * e a versão pedida pode ter sido substituída depois.
 */
export const fetchAuditoriaProposta = (propostaId: string) =>
  req<AuditoriaProposta>(
    () => api.get(`/auditoria/propostas/${propostaId}`),
    'Erro ao carregar a proposta',
  );

/** Pedidos gerados no ERP, com os itens efetivamente enviados. */
export const fetchAuditoriaPedidos = (cotacaoId: string) =>
  req<AuditoriaPedidos>(
    () => api.get(`/auditoria/cotacoes/${cotacaoId}/pedidos`),
    'Erro ao carregar os pedidos gerados',
  );

/** O AGORA da cotação: uma linha por item, com a decisão que vale neste momento. */
export const fetchAuditoriaEscolhas = (cotacaoId: string) =>
  req<AuditoriaEscolhasAtuais>(
    () => api.get(`/auditoria/cotacoes/${cotacaoId}/escolhas`),
    'Erro ao carregar as escolhas atuais',
  );

export const fetchAuditoriaConvites = (cotacaoId: string) =>
  req<AuditoriaConvites>(
    () => api.get(`/auditoria/cotacoes/${cotacaoId}/convites`),
    'Erro ao carregar os fornecedores convidados',
  );

export interface CriarCotacaoPayload {
  /**
   * A requisição aprovada que esta cotação atende — **uma só** (at-11, 28/09/2026).
   *
   * ⚠️ O backend **revalida** este número na origem e grava o que ela devolver: requisição
   * reprovada ou cancelada depois de a lista carregar é recusada com 409. O campo
   * `solicitacoesExtras` (item 21) deixou de existir nos dois lados.
   */
  solicitacaoNumero: string;
  placa?: string;
  veiculo?: string;
  presetId?: string;
  prazoRespostaAte: string;
  /**
   * Itens vindos da solicitação, repassados como chegam.
   *
   * ⚠️ O backend do Portal roda `ValidationPipe` com `forbidNonWhitelisted: true`: campo
   * que exista aqui e **não** no `CriarCotacaoItemDto` derruba a criação com 400. Ao somar
   * campo à solicitação, somar nos dois lados (12/08/2026: `observacao` + as referências).
   */
  itens: {
    codigoProduto: string;
    descricao: string;
    quantidade: number;
    unidade: string;
    observacao?: string | null;
    valorOrcado?: number | null;
    mediaCompras?: number | null;
    mediaComprasQtd?: number | null;
    // orcamento-cilia/at-15: somados também no `CriarCotacaoItemDto` do Portal (regra acima).
    precoOnline?: number | null;
    precoOnlineCanal?: string | null;
    precoOnlineUrl?: string | null;
    precoOnlinePesquisadoEm?: string | null;
    precoOnlineConferir?: boolean | null;
  }[];
  /** O convite sai só pelo WhatsApp (at-17); o servidor ignora `canais`, por isso ele não vai. */
  /** `itens` = POSIÇÕES no array `itens` acima que este fornecedor recebe; ausente = todos (at-18). */
  fornecedores: { cnpj: string; itens?: number[] }[];
}

export const criarCotacao = (payload: CriarCotacaoPayload) =>
  req<CotacaoDetalhe>(() => api.post('/cotacoes', payload), 'Erro ao criar a cotação');

export const enviarCotacao = (id: string) =>
  req<RespostaEnvio>(() => api.post(`/cotacoes/${id}/enviar`), 'Erro ao enviar os convites');

export const encerrarRespostas = (id: string) =>
  req<CotacaoDetalhe>(() => api.post(`/cotacoes/${id}/encerrar-respostas`), 'Erro ao encerrar as respostas');

export const reabrirCotacao = (id: string, novoPrazo: string) =>
  req<CotacaoDetalhe>(() => api.post(`/cotacoes/${id}/reabrir`, { novoPrazo }), 'Erro ao reabrir a cotação');

export const cancelarCotacao = (id: string, motivo: string) =>
  req<CotacaoDetalhe>(() => api.post(`/cotacoes/${id}/cancelar`, { motivo }), 'Erro ao cancelar a cotação');

export const recotarItens = (id: string, itemIds: string[]) =>
  req<CotacaoDetalhe>(() => api.post(`/cotacoes/${id}/recotar`, { itemIds }), 'Erro ao recotar os itens');

// ── Convites ──────────────────────────────────────────────────────────────────

// ⚠️ **Novo link** rotaciona o token (o anterior deixa de valer). **Reenviar** reaproveita o
// link vigente desde 12/08/2026 e só rotaciona se não houver token recuperável — as duas
// respostas dizem qual foi o caso em `linkMudou`.
export const reenviarConvite = (conviteId: string) =>
  req<RespostaConvite>(() => api.post(`/convites/${conviteId}/reenviar`), 'Erro ao reenviar o convite');

export const rotacionarConvite = (conviteId: string) =>
  req<RespostaConvite>(() => api.post(`/convites/${conviteId}/rotacionar`), 'Erro ao gerar um link novo');

/**
 * Link VIGENTE do convite (12/08/2026) — leitura pura, não invalida nada.
 *
 * Só existe porque o token passou a ser guardado cifrado no Portal: antes, ver o link
 * obrigava a gerar outro. `disponivel: false` = convite anterior à mudança (ou chave
 * trocada): não há link a exibir, e a tela oferece gerar um novo.
 */
export const obterLinkVigente = (conviteId: string) =>
  req<LinkVigente>(
    () => api.get(`/convites/${conviteId}/link`),
    'Erro ao obter o link do fornecedor',
  );

export const obterLinkWhatsapp = (conviteId: string) =>
  req<{ url: string; mensagem: string }>(
    () => api.get(`/convites/${conviteId}/whatsapp-link`),
    'Erro ao montar a mensagem de WhatsApp',
  );

/**
 * Envio ATIVO por WhatsApp (at-07): quem envia é o servidor, pelo número oficial da empresa.
 *
 * ⚠️ Irmão do `obterLinkWhatsapp` acima, com duas diferenças que importam para a tela: aqui a
 * mensagem sai de fato (não há nada a abrir depois), e o **link vigente é reaproveitado** — o
 * caminho manual rotaciona o token a cada clique, este não. Exige a integração ativa; sem ela
 * o backend responde 409 orientando o envio manual, em vez de trocar o canal por baixo.
 */
export const enviarWhatsappConvite = (conviteId: string) =>
  req<RespostaEnvioWhatsapp>(
    () => api.post(`/convites/${conviteId}/whatsapp-enviar`),
    'Erro ao enviar o convite por WhatsApp',
  );

// ── Integrações e configurações do módulo ─────────────────────────────────────

export const obterStatusOctadesk = () =>
  req<StatusOctadesk>(
    () => api.get('/integracoes/octadesk'),
    'Erro ao carregar o estado da integração de mensagens',
  );

export const fetchSettingsPortal = () =>
  req<Record<string, string>>(() => api.get('/settings'), 'Erro ao carregar as configurações');

export const atualizarSettingPortal = (chave: string, valor: string) =>
  req<{ ok: boolean; message?: string }>(
    () => api.put(`/settings/${chave}`, { value: valor }),
    'Erro ao salvar a configuração',
  );

/** Uma forma de pagamento ou conta do Nasajon (portal-cotacoes/at-15). */
export interface CadastroDeCobranca {
  id: string;
  codigo: string;
  descricao: string;
}

/** Como uma opção de forma de pagamento vira a cobrança do pedido no Nasajon (at-15). */
export interface RegraDeCobranca {
  formaPagamento: string;
  conta: string;
  parcelas: number;
  diasPrimeiraParcela: number;
  intervaloDias: number;
}

export const fetchCadastrosDeCobranca = () =>
  req<{ formas: CadastroDeCobranca[]; contas: CadastroDeCobranca[] }>(
    () => api.get('/settings/cobranca-nasajon/cadastros'),
    'Erro ao carregar as formas de pagamento e contas do Nasajon',
  );

// ── Comparação e cesta ────────────────────────────────────────────────────────

export const fetchComparacao = (cotacaoId: string) =>
  req<Comparacao>(() => api.get(`/cotacoes/${cotacaoId}/comparacao`), 'Erro ao carregar a comparação');

export const salvarDecisoes = (cotacaoId: string, itens: DecisaoPayload[]) =>
  req<Comparacao>(() => api.put(`/cotacoes/${cotacaoId}/decisoes`, { itens }), 'Erro ao salvar as decisões');

export const fecharCesta = (cotacaoId: string) =>
  req<{ status: 'AGUARDANDO_APROVACAO' | 'GERANDO_PEDIDOS'; itensDivergentes: number }>(
    () => api.post(`/cotacoes/${cotacaoId}/fechar-cesta`),
    'Erro ao fechar a cesta',
  );

/** Um fornecedor vencedor e as formas de pagamento que ele marcou (portal-cotacoes/at-15). */
export interface FornecedorDoPedido {
  cnpj: string;
  nome: string;
  total: number;
  jaEnviado: boolean;
  /**
   * Todas as formas configuradas (at-16). `marcadaPeloFornecedor` só vale para proposta anterior à
   * at-16, quando o fornecedor marcava as formas: hoje é informação, não restrição.
   */
  formas: { opcao: string; configurada: boolean; marcadaPeloFornecedor?: boolean }[];
}

export const fetchPreparoPedidos = (cotacaoId: string) =>
  req<{ fornecedores: FornecedorDoPedido[] }>(
    () => api.get(`/cotacoes/${cotacaoId}/pedidos/preparo`),
    'Erro ao carregar os fornecedores dos pedidos',
  );

/** `formasPagamento`: a opção escolhida para cada pedido, por CNPJ do fornecedor. */
export const gerarPedidos = (cotacaoId: string, formasPagamento: Record<string, string>) =>
  req<{ concluida: boolean; pedidos: unknown[] }>(
    () => api.post(`/cotacoes/${cotacaoId}/gerar-pedidos`, { formasPagamento }),
    'Erro ao gerar os pedidos',
  );

export const fetchPedidos = (cotacaoId: string) =>
  req<PedidoGerado[]>(() => api.get(`/cotacoes/${cotacaoId}/pedidos`), 'Erro ao carregar os pedidos');

export const reenviarPedido = (pedidoId: string) =>
  req<unknown>(() => api.post(`/pedidos/${pedidoId}/reenviar`), 'Erro ao reenviar o pedido');

// ── E-mails ao fornecedor depois da decisão (at-11) ───────────────────────────

/*
 * ⚠️ As duas rotas de envio respondem **200 com `sucesso: false`** quando o SMTP recusa o
 * e-mail — não é erro de requisição, é desfecho do envio, e o registro dele fica gravado. Quem
 * chama precisa olhar `sucesso`, nunca só a ausência de exceção: emitir "enviado" sobre um
 * `sucesso: false` é exatamente o falso sucesso da at-60, aqui com consequência externa.
 */
export const enviarEmailEspelhoNf = (cotacaoId: string, cnpj: string) =>
  req<EnvioDeEmail>(
    () => api.post(`/cotacoes/${cotacaoId}/fornecedores/${cnpj}/espelho-nf`),
    'Erro ao enviar o e-mail do espelho da nota fiscal',
  );

/** Resumo da proposta (at-16): o comprador manda ao fornecedor o que ele respondeu. Mesmo desfecho `sucesso`. */
export const enviarEmailResumoProposta = (cotacaoId: string, cnpj: string) =>
  req<EnvioDeEmail>(
    () => api.post(`/cotacoes/${cotacaoId}/fornecedores/${cnpj}/resumo-proposta`),
    'Erro ao enviar o resumo da proposta',
  );

// ── at-17 (teste de Compras de 29/10/2026) ────────────────────────────────────

/** "Atualizar agora": o módulo de Requisições reconsulta o Nasajon sem esperar os 10 minutos. */
export const atualizarRequisicoes = () =>
  req<{ verificadas: number; atualizadas: number; atualizadoEm: string }>(
    () => api.post('/solicitacoes/atualizar'),
    'Não foi possível atualizar as requisições agora',
  );

/** Convida fornecedor novo a uma cotação já enviada. O convite sai na hora, pelo WhatsApp. */
export const adicionarFornecedoresACotacao = (cotacaoId: string, cnpjs: string[], cotacaoItemIds?: string[]) =>
  req<RespostaEnvio>(
    // `cotacaoItemIds` só quando não são todos os itens (at-18): ausente, o backend dá todos.
    () => api.post(`/cotacoes/${cotacaoId}/convites`, cotacaoItemIds ? { cnpjs, cotacaoItemIds } : { cnpjs }),
    'Erro ao convidar o fornecedor',
  );

/** Uma alteração entre duas versões da proposta, calculada no servidor. */
export interface AlteracaoDaProposta {
  item: string | null;
  campo: string;
  antes: string | number | boolean | null;
  depois: string | number | boolean | null;
}

export interface VersaoDaProposta {
  id: string;
  versao: number;
  status: 'ATIVA' | 'SUBSTITUIDA';
  enviadaEm: string;
  cabecalho: { frete: number | null; condicaoPagamento: string | null; observacao: string | null };
  ofertas: {
    cotacaoItemId: string;
    descricao: string;
    variante: number;
    cotou: boolean;
    condicao: string | null;
    precoUnitario: number | null;
    prazoEntregaDias: number | null;
    marcaFabricante: string | null;
    observacaoItem: string | null;
    total: number | null;
  }[];
  alteracoes: AlteracaoDaProposta[];
}

/** Histórico das correções da proposta de um fornecedor. Sem catch: "sem correções" é afirmação. */
export const fetchHistoricoDaProposta = (conviteId: string) =>
  req<{ fornecedorNome: string; versoes: VersaoDaProposta[] }>(
    () => api.get(`/convites/${conviteId}/propostas`),
    'Erro ao carregar o histórico da proposta',
  );

/** Foto do veículo enviada do computador (veículo de oficina externa não tem checklist). */
export const enviarFotoDoVeiculo = (cotacaoId: string, foto: File) => {
  const form = new FormData();
  form.append('foto', foto);
  return req<{ ok: boolean; atual: string | null }>(
    () => api.post(`/cotacoes/${cotacaoId}/foto-do-veiculo/upload`, form),
    'Erro ao enviar a foto do veículo',
  );
};

/** Uma foto que o comprador mostra ao fornecedor (dano, oficina externa). */
export interface FotoParaFornecedor {
  id: string;
  cotacaoItemId: string | null;
  nomeOriginal: string;
  origem: 'UPLOAD' | 'CHECKLIST';
  enviadoPorNome: string | null;
  createdAt: string;
  url: string | null;
}

export const fetchFotosParaFornecedor = (cotacaoId: string) =>
  req<FotoParaFornecedor[]>(
    () => api.get(`/cotacoes/${cotacaoId}/fotos-fornecedor`),
    'Erro ao carregar as fotos para o fornecedor',
  );

export const enviarFotoParaFornecedor = (cotacaoId: string, foto: File, cotacaoItemId?: string) => {
  const form = new FormData();
  form.append('foto', foto);
  if (cotacaoItemId) form.append('cotacaoItemId', cotacaoItemId);
  return req<{ id: string; url: string | null }>(
    () => api.post(`/cotacoes/${cotacaoId}/fotos-fornecedor`, form),
    'Erro ao enviar a foto',
  );
};

export const copiarFotoDoChecklistParaFornecedor = (cotacaoId: string, midiaId: string, cotacaoItemId?: string) =>
  req<{ id: string; url: string | null }>(
    () => api.post(`/cotacoes/${cotacaoId}/fotos-fornecedor/do-checklist`, { midiaId, ...(cotacaoItemId ? { cotacaoItemId } : {}) }),
    'Erro ao copiar a foto do checklist',
  );

export const removerFotoParaFornecedor = (fotoId: string) =>
  req<{ ok: boolean }>(() => api.delete(`/fotos-fornecedor/${fotoId}`), 'Erro ao remover a foto');

/** Uma foto do checklist de entrada do veículo (at-16), vinda do SOAC. */
export interface FotoDeEntrada {
  id: string;
  rotulo: string | null;
  url: string;
  mimeType: string;
}

/**
 * As fotos do checklist de entrada para escolher, e a escolhida (at-16). Sem catch: "a requisição
 * não tem atendimento", "o atendimento não tem foto" e "não consegui consultar" são três estados
 * diferentes, e quem os distingue é a tela.
 */
export const fetchFotosDoVeiculo = (cotacaoId: string) =>
  req<{ disponivel: boolean; motivo: 'SEM_ATENDIMENTO' | null; fotos: FotoDeEntrada[]; atual: string | null }>(
    () => api.get(`/cotacoes/${cotacaoId}/fotos-do-veiculo`),
    'Erro ao carregar as fotos do veículo',
  );

export const escolherFotoDoVeiculo = (cotacaoId: string, midiaId: string | null) =>
  req<{ ok: boolean; atual: string | null }>(
    () => api.put(`/cotacoes/${cotacaoId}/foto-do-veiculo`, { midiaId }),
    'Erro ao escolher a foto do veículo',
  );

export const enviarEmailAutorizacaoFaturamento = (pedidoId: string) =>
  req<EnvioDeEmail>(
    () => api.post(`/pedidos/${pedidoId}/autorizacao-faturamento`),
    'Erro ao enviar o e-mail de autorização de faturamento',
  );

export const fetchEmailsFornecedor = (cotacaoId: string) =>
  req<EmailFornecedorEnviado[]>(
    () => api.get(`/cotacoes/${cotacaoId}/emails-fornecedor`),
    'Erro ao carregar os e-mails enviados ao fornecedor',
  );

// ── Imagens internas da cotação (at-11) ──────────────────────────────────────

export const fetchStatusAnexos = () =>
  req<{ configurado: boolean }>(
    () => api.get('/anexos/status'),
    'Erro ao verificar o armazenamento de imagens',
  );

export const fetchAnexos = (cotacaoId: string) =>
  req<CotacaoAnexo[]>(() => api.get(`/cotacoes/${cotacaoId}/anexos`), 'Erro ao carregar as imagens');

/*
 * ⚠️ O `Content-Type` NÃO é declarado: o axios monta o `multipart/form-data` com o `boundary`
 * a partir do próprio `FormData`. Fixá-lo à mão produz um corpo que o multer não consegue
 * separar, e o erro aparece como "arquivo ausente" — longe da causa.
 */
export const anexarImagem = (cotacaoId: string, arquivo: File, cotacaoItemId?: string) => {
  const form = new FormData();
  form.append('imagem', arquivo);
  if (cotacaoItemId) form.append('cotacaoItemId', cotacaoItemId);
  return req<CotacaoAnexo>(
    () => api.post(`/cotacoes/${cotacaoId}/anexos`, form),
    'Erro ao anexar a imagem',
  );
};

export const removerAnexo = (anexoId: string) =>
  req<{ ok: boolean }>(() => api.delete(`/anexos/${anexoId}`), 'Erro ao remover a imagem');

// ── Aprovações (gestor) ───────────────────────────────────────────────────────

export const fetchFilaAprovacoes = () =>
  req<AprovacaoFilaItem[]>(() => api.get('/aprovacoes'), 'Erro ao carregar a fila de aprovações');

export const fetchAprovacaoDetalhe = (cotacaoId: string) =>
  req<AprovacaoDetalhe>(() => api.get(`/aprovacoes/cotacao/${cotacaoId}`), 'Erro ao carregar a aprovação');

export const aprovarItem = (aprovacaoId: string) =>
  req<unknown>(() => api.post(`/aprovacoes/${aprovacaoId}/aprovar`), 'Erro ao aprovar o item');

export const reprovarItem = (aprovacaoId: string, comentario: string) =>
  req<unknown>(() => api.post(`/aprovacoes/${aprovacaoId}/reprovar`, { comentario }), 'Erro ao reprovar o item');

/** `apenasPreset`: só as escolhas que seguiram o preset (28/10/2026). */
export const aprovarTodos = (cotacaoId: string, opcoes: { apenasPreset?: boolean } = {}) =>
  req<unknown>(() => api.post(`/aprovacoes/cotacao/${cotacaoId}/aprovar-todos`, opcoes), 'Erro ao aprovar os itens');

// ── Fornecedores, presets, gestores, alertas ──────────────────────────────────

export const fetchFornecedores = (params: { search?: string; page?: number; pageSize?: number } = {}) =>
  req<{ data: Fornecedor[]; total: number }>(
    () => api.get('/fornecedores', { params }),
    'Erro ao listar fornecedores',
  );

export const importarFornecedores = () =>
  req<{ importados: number; atualizados: number; paginas: number; parcial: boolean; aviso?: string }>(
    () => api.post('/fornecedores/importar'),
    'Erro ao importar fornecedores do ERP',
  );

export const criarContatoFornecedor = (
  cnpj: string,
  payload: { nome?: string; telefone?: string; email?: string; principal?: boolean },
) => req<unknown>(() => api.post(`/fornecedores/${cnpj}/contatos`, payload), 'Erro ao cadastrar o contato');

export const atualizarContatoFornecedor = (
  id: string,
  payload: { nome?: string; telefone?: string; email?: string; principal?: boolean },
) => req<unknown>(() => api.patch(`/contatos/${id}`, payload), 'Erro ao atualizar o contato');

export const removerContatoFornecedor = (id: string) =>
  req<unknown>(() => api.delete(`/contatos/${id}`), 'Erro ao remover o contato');

export const fetchPresets = () => req<Preset[]>(() => api.get('/presets'), 'Erro ao carregar os presets');

export const atualizarPreset = (
  id: string,
  payload: { nome?: string; descricao?: string; pesoPreco: number; pesoPrazo: number; pesoQualidade: number },
) => req<Preset>(() => api.put(`/presets/${id}`, payload), 'Erro ao salvar o preset');

export const fetchGestores = () => req<GestorCompras[]>(() => api.get('/gestores'), 'Erro ao carregar os gestores');

export const souGestor = () =>
  req<{ gestor: boolean }>(() => api.get('/gestores/me'), 'Erro ao verificar o papel de gestor');

/**
 * Define a lista de gestores de compras (`PUT /gestores`, **ADMIN puro** no Portal).
 *
 * ⚠️ **Declarativa**: a lista enviada SUBSTITUI a atual — o backend faz upsert dos
 * enviados e apaga os ausentes, numa transação. Quem deve continuar gestor precisa vir no
 * mesmo payload; mandar só o novo remove todos os outros.
 *
 * O `nome` é uma cópia (o Portal não tem cadastro de usuários — o JWT é o do SOAC, ADR
 * 0014), e é o que a fila de aprovação exibe. Sem ele a lista mostra "Nome não disponível".
 */
export const definirGestores = (gestores: { userId: string; nome?: string }[]) =>
  req<GestorCompras[]>(() => api.put('/gestores', { gestores }), 'Erro ao salvar os gestores de compras');

export const fetchPainelAlertas = () =>
  req<PainelAlertas>(() => api.get('/alertas/painel'), 'Erro ao carregar o painel de entregas');

// ── Notificações do módulo (at-06) ────────────────────────────────────────────

/**
 * Avisos do Portal para o usuário logado.
 *
 * ⚠️ **Estas rotas existiam desde o MVP e ninguém as chamava** (`BUG-V02`): o backend
 * gravava avisos em 9 pontos do fluxo — gestor com aprovação pendente, comprador com
 * proposta recebida, veredito, convite bloqueado, pedidos gerados — e tudo morria no
 * banco. É a mesma classe do logout da at-72 do SOAC: rota sem consumidor declarado é
 * suspeita de não estar sendo chamada.
 *
 * Paginada no molde do sino do SOAC (`getMyNotifications`): a resposta traz `total` para o
 * "Carregar mais (N de M)" e `naoLidas` para o badge — que é do conjunto inteiro, não da
 * página. `somenteNaoLidas` filtra NA CONSULTA, nunca depois do corte da página.
 */
export const fetchNotificacoesPortal = (
  params: { page?: number; limit?: number; somenteNaoLidas?: boolean } = {},
) =>
  req<NotificacoesResposta>(
    () =>
      api.get('/notificacoes', {
        params: {
          ...(params.page ? { page: params.page } : {}),
          ...(params.limit ? { limit: params.limit } : {}),
          ...(params.somenteNaoLidas ? { somenteNaoLidas: 'true' } : {}),
        },
      }),
    'Erro ao carregar os avisos de cotações',
  );

/**
 * Só a contagem de não lidas — é o badge do menu lateral (at-06).
 *
 * Reaproveita a rota da listagem com `limit: 1` em vez de criar rota nova de contagem: o
 * `naoLidas` da resposta já é o total do usuário (não da página), então a única diferença
 * seria trafegar um registro a menos. Rota nova custaria baseline de contrato e doc para
 * economizar isso.
 */
export const contarAvisosNaoLidosPortal = async (): Promise<number> => {
  const resposta = await fetchNotificacoesPortal({ page: 1, limit: 1 });
  return resposta.naoLidas;
};

export const marcarNotificacaoPortalLida = (id: string) =>
  req<{ ok: boolean }>(() => api.patch(`/notificacoes/${id}/lida`), 'Erro ao marcar o aviso como lido');

export const marcarTodasNotificacoesPortalLidas = () =>
  req<{ ok: boolean; marcadas: number }>(
    () => api.patch('/notificacoes/todas/lidas'),
    'Erro ao marcar os avisos como lidos',
  );

/**
 * Avisa o COMPRADOR de que um complemento do orçamento tirou (ou reduziu) peça que está
 * numa cotação em andamento (at-06 do Cilia, 17/08/2026).
 *
 * **Quem chama é a tela do Cilia**, com o JWT do usuário logado — é o desenho aprovado pelo
 * Victor em 16/08/2026 e a aplicação do ADR 0014 da Oficina: a detecção só é possível no
 * Cilia (a solicitação que o Portal recebe carrega apenas o ACRÉSCIMO do complemento;
 * remoção e redução existem só no diff do orçamento), e a SPA é a única que tem sessão nos
 * dois módulos. Nenhuma chamada backend-a-backend.
 *
 * ⚠️ O texto do aviso NÃO é enviado daqui: manda-se dado estruturado e o backend do Portal
 * monta título e mensagem. Texto pronto vindo do cliente permitiria gravar conteúdo
 * arbitrário assinado como aviso do sistema.
 */
export const registrarAvisoComplemento = (payload: {
  cotacaoId: string;
  orcamentoNumero: string;
  orcamentoVersao: number;
  pecas: Array<{
    codigo: string;
    descricao: string;
    motivo: 'REMOVIDA' | 'REDUZIDA';
    detalhe?: string;
  }>;
}) =>
  req<{ ok: boolean; deduplicada: boolean }>(
    () => api.post('/notificacoes/aviso-complemento', payload),
    'Erro ao avisar quem está cotando',
  );

// ── Rotas públicas do fornecedor (sem JWT) ────────────────────────────────────

export const validarLinkPublico = (token: string) =>
  req<CotacaoPublicaValidacao>(
    () => publicApi.get(`/public/cotacao/${token}`),
    'Não foi possível abrir este link',
  );

/**
 * O conteúdo da cotação para o fornecedor. Desde a at-17 (ADR 0015 do Portal) sem senha: o link é a
 * única credencial (pedido da Alessandra no teste de 29/10/2026).
 */
export const verificarEObterCotacaoPublica = (token: string) =>
  req<RespostaVerificacaoPublica>(
    () => publicApi.post(`/public/cotacao/${token}/verificar`, {}),
    'Não foi possível abrir a cotação',
  );

/** Envia uma foto da peça (at-16). A foto nasce solta e é presa à oferta no envio da proposta. */
export const enviarFotoPublica = (token: string, foto: File) => {
  const form = new FormData();
  form.append('foto', foto);
  return req<{ id: string; url: string | null }>(
    () => publicApi.post(`/public/cotacao/${token}/fotos`, form),
    'Não foi possível enviar a foto',
  );
};

export const enviarPropostaPublica = (
  token: string,
  payload: {
    /**
     * Frete da proposta inteira, obrigatório desde a at-11 (28/09/2026). `0` significa "sem
     * frete ou já incluso" — é resposta, não ausência.
     */
    frete: number;
    condicaoPagamento?: string;
    observacao?: string;
    itens: PropostaPublicaItem[];
  },
) =>
  req<{ ok: boolean; versao: number }>(
    () => publicApi.post(`/public/cotacao/${token}/proposta`, payload),
    'Erro ao enviar a proposta',
  );

export const declinarCotacaoPublica = (token: string) =>
  req<{ ok: boolean }>(
    () => publicApi.post(`/public/cotacao/${token}/declinar`, {}),
    'Erro ao registrar a recusa',
  );
